// frontend/src/components/Header.tsx
import { Link, useNavigate } from 'react-router-dom';
import { useMemo, useRef, useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Ion from './Ion';
import './header.css';

type NotificationItem = { id: string; title: string; time?: string; href?: string };

/** =========================================================
 * Fallback de notificaciones (sin contexto)
 * ======================================================= */
function useNotificationsFallback(): { items: NotificationItem[] } {
  const items: NotificationItem[] = Array.isArray((window as any).__NOTIFICATIONS__)
    ? (window as any).__NOTIFICATIONS__
    : [];
  return { items };
}

/** =========================================================
 * Permite usar NotificationsContext si existe; si no, fallback
 * En Vite (ESM) `require` no existe en runtime, pero lo envolvemos
 * en try/catch y además lo declaramos como any para el tipo.
 * ======================================================= */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const require: any;

let useNotifications: () => { items: NotificationItem[] };
try {
  const real = require('../context/NotificationsContext');
  useNotifications = real?.useNotifications ?? useNotificationsFallback;
} catch {
  useNotifications = useNotificationsFallback;
}

type Props = {
  onToggleSidebarMobile?: () => void;
};

export default function Header({ onToggleSidebarMobile }: Props) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { items: notifications } = useNotifications();

  const [term, setTerm] = useState('');
  const [openNotif, setOpenNotif] = useState(false);
  const [openUser, setOpenUser] = useState(false);

  const notifRef = useRef<HTMLDivElement>(null);
  const userRef = useRef<HTMLDivElement>(null);

  // Cerrar menús al hacer click fuera o con ESC
  useEffect(() => {
    function handleDocClick(e: MouseEvent) {
      const t = e.target as Node;
      if (openNotif && notifRef.current && !notifRef.current.contains(t)) setOpenNotif(false);
      if (openUser && userRef.current && !userRef.current.contains(t)) setOpenUser(false);
    }
    function handleEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpenNotif(false);
        setOpenUser(false);
      }
    }
    document.addEventListener('mousedown', handleDocClick);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleDocClick);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [openNotif, openUser]);

  // Nombre a mostrar
  const displayName = useMemo(() => {
    const full =
      user?.full_name ||
      user?.name ||
      [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim();
    return full || user?.email || 'Usuario';
  }, [user]);

  const initials = useMemo(() => {
    const base = (displayName || '').trim();
    if (!base) return 'U';
    const [a = '', b = ''] = base.split(/\s+/);
    return (a[0] || '').concat(b[0] || '').toUpperCase() || 'U';
  }, [displayName]);

  const handleLogout = (e: React.MouseEvent) => {
    e.preventDefault();
    try {
      logout();
    } finally {
      navigate('/', { replace: true });
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = term.trim();
    navigate(`/devioz/empresas${q ? `?q=${encodeURIComponent(q)}` : ''}`);
  };

  const notifCount = notifications.length;

  // Estilos seguros para dropdowns (compatible claro/oscuro)
  const dropdownStyles: React.CSSProperties = {
    backgroundColor: 'var(--ca-card, #ffffff)',
    color: 'var(--ca-fg, #111827)',
    border: '1px solid var(--ca-card-border, rgba(0,0,0,.08))',
    boxShadow: 'var(--ca-shadow, 0 10px 24px rgba(0,0,0,.15))',
    minWidth: 280,
  };
  const headerStyles: React.CSSProperties = { color: 'var(--ca-muted, #6b7280)' };
  const itemStyles: React.CSSProperties = {
    color: 'var(--ca-fg, #111827)',
    background: 'transparent',
  };
  const dividerStyles: React.CSSProperties = {
    borderTop: '1px solid var(--ca-card-border, rgba(0,0,0,.08))',
    margin: 0,
  };

  return (
    <div id="header" className="app-header app-header--grid">
      {/* IZQUIERDA */}
      <div className="header-left">
        <button
          type="button"
          className="navbar-mobile-toggler d-inline-flex d-md-none"
          onClick={onToggleSidebarMobile}
          aria-label="Abrir menú lateral en móvil"
        >
          <span className="icon-bar" />
          <span className="icon-bar" />
          <span className="icon-bar" />
        </button>

        <Link to="/devioz/dashboard" className="navbar-brand d-flex align-items-center">
          <span className="navbar-logo d-flex align-items-center justify-content-center me-2">
            <Ion name="cloud" />
          </span>
          <img
            src="/assets/img/logo/Devioz.png"
            alt="Devioz"
            className="brand-devioz"
            style={{ height: 22, objectFit: 'contain', verticalAlign: 'middle' }}
          />
          <b className="ms-2 me-1">Devioz</b>
          <span className="text-opacity-75">Gerente</span>
        </Link>
      </div>

      {/* CENTRO */}
      <div className="header-center">
        <form onSubmit={handleSearch} role="search" className="search-wrap">
          <input
            type="text"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            className="form-control search-input"
            placeholder="Buscar empresas..."
            aria-label="Buscar empresas"
          />
          <button type="submit" className="btn btn-search" aria-label="Buscar">
            <Ion name="search" />
          </button>
        </form>
      </div>

      {/* DERECHA */}
      <div className="header-right d-flex align-items-center">
        {/* Notificaciones */}
        <div className="navbar-item dropdown me-2 dropdown-react" ref={notifRef}>
          <button
            type="button"
            className={`navbar-link icon notif-pill ${openNotif ? 'is-open' : ''}`}
            aria-label="Notificaciones"
            aria-haspopup="menu"
            aria-expanded={openNotif}
            onClick={() => {
              setOpenNotif((v) => !v);
              setOpenUser(false);
            }}
          >
            <Ion name="notifications" />
            <span
              className="badge"
              aria-label={`${notifCount} notificaciones`}
              style={{ minWidth: 18, textAlign: 'center' }}
            >
              {notifCount}
            </span>
          </button>

          <div
            role="menu"
            className={`dropdown-menu-react dropdown-menu-end-react ${openNotif ? 'open' : ''}`}
            style={dropdownStyles}
          >
            <div className="dropdown-header px-3 py-2" style={headerStyles}>
              Notificaciones
            </div>
            {notifCount ? (
              notifications.slice(0, 6).map((n) => (
                <a
                  key={n.id}
                  className="dropdown-item-react media-react"
                  href={n.href || '#'}
                  style={itemStyles}
                >
                  <div className="media-left-react" aria-hidden="true">
                    <i className="fa fa-info media-object-react" />
                  </div>
                  <div className="media-body-react">
                    <div className="media-heading-react">{n.title}</div>
                    {n.time ? <div className="media-time-react">{n.time}</div> : null}
                  </div>
                </a>
              ))
            ) : (
              <div className="dropdown-empty-react px-3 py-2" style={headerStyles}>
                Sin notificaciones
              </div>
            )}
            {notifCount ? (
              <div className="dropdown-footer-react px-3 py-2">
                <Link to="/devioz/notificaciones" className="text-decoration-none" style={itemStyles}>
                  Ver todo
                </Link>
              </div>
            ) : null}
          </div>
        </div>

        {/* Usuario */}
        <div className="navbar-item dropdown dropdown-react" ref={userRef}>
          <button
            type="button"
            className={`navbar-link d-inline-flex align-items-center user-pill ${openUser ? 'is-open' : ''}`}
            aria-haspopup="menu"
            aria-expanded={openUser}
            onClick={() => {
              setOpenUser((v) => !v);
              setOpenNotif(false);
            }}
          >
            <span className="avatar-circle" aria-hidden="true">
              {initials}
            </span>
            {/* Nombre visible en md+ para no romper layout en móvil */}
            <span className="user-name ms-2 d-none d-md-inline" style={{ color: 'var(--ca-fg, #111827)' }}>
              {displayName}
            </span>
            <span className="ms-2 d-inline-flex align-items-center">
              <Ion name="chevron-down" />
            </span>
          </button>

          <div
            role="menu"
            className={`dropdown-menu-react dropdown-menu-end-react user-dropdown-react ${openUser ? 'open' : ''}`}
            style={dropdownStyles}
          >
            <div className="dropdown-header px-3 py-2" style={headerStyles}>
              Sesión
            </div>

            <button
              type="button"
              className="dropdown-item-react text-start w-100 px-3 py-2"
              onClick={() => navigate('/devioz/perfil')}
              style={itemStyles}
            >
              <i className="fa fa-user me-2" aria-hidden="true" />
              Perfil
            </button>

            <button
              type="button"
              className="dropdown-item-react text-start w-100 px-3 py-2"
              onClick={() => navigate('/devioz/configuracion')}
              style={itemStyles}
            >
              <i className="fa fa-cog me-2" aria-hidden="true" />
              Configuración
            </button>

            <div className="dropdown-divider-react" style={dividerStyles} />

            <button
              type="button"
              className="dropdown-item-react text-start w-100 px-3 py-2"
              onClick={handleLogout}
              style={itemStyles}
            >
              <i className="fa fa-sign-out-alt me-2" aria-hidden="true" />
              Cerrar sesión
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
